import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Filter,
  MapPin,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
} from 'lucide-react';
import '../Employees/Employees.css';
import { getBranches } from '../hrisApi';

const statusClass = (value) => String(value || 'Active').toLowerCase().replace(/\s+/g, '-');

const toBranchRow = (record, index) => ({
  id: record.id || record.branchCode || record.branchName || `branch-${index}`,
  name: record.branchName || record.name || '—',
  code: record.branchCode || record.code || '—',
  city: record.city || record.location || '—',
  manager: record.managerName || record.manager || '—',
  employeeCount: Number(record.employeeCount || record.employees || 0),
  status: record.status || 'Active',
  address: record.address || '—',
});

function Status({ value }) {
  return <span className={`employee-status ${statusClass(value)}`}><i />{value}</span>;
}

const Branch = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('All cities');
  const [status, setStatus] = useState('All statuses');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const [showFilters, setShowFilters] = useState(true);

  const load = () => {
    setLoading(true);
    setError(null);
    getBranches({ page: 0, size: 500 })
      .then((result) => {
        const rows = (result.content || []).map(toBranchRow);
        setBranches(rows);
      })
      .catch(() => setError('Could not load branches from the server.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const unique = (key) => [...new Set(branches.map((item) => item[key]).filter((value) => value && value !== '—'))];

  const filtered = useMemo(() => branches.filter((item) => {
    const haystack = `${item.name} ${item.code} ${item.city} ${item.manager} ${item.address}`.toLowerCase();
    return (!query || haystack.includes(query.toLowerCase()))
      && (city === 'All cities' || item.city === city)
      && (status === 'All statuses' || item.status === status);
  }), [branches, query, city, status]);

  const visible = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const activeFilters = [city !== 'All cities', status !== 'All statuses'].filter(Boolean).length;
  const totalEmployees = branches.reduce((sum, item) => sum + item.employeeCount, 0);

  const reset = () => {
    setQuery('');
    setCity('All cities');
    setStatus('All statuses');
    setPage(1);
  };

  const select = (event, setter) => {
    setter(event.target.value);
    setPage(1);
  };

  const changeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  return (
    <div className="employee-directory-page hris-master-page">
      <header className="employee-page-header">
        <div>
          <div className="eyebrow"><Building2 size={15} /> HUMAN RESOURCES</div>
          <h1>Branch</h1>
          <p>Directory cabang untuk melihat lokasi, manager, status, dan jumlah karyawan per branch.</p>
        </div>
        <div className="source-state">
          <span><i /> {loading ? 'Loading…' : error ? 'Offline' : 'Live data'}</span>
          <small>{error || 'Synced from HRIS backend'}</small>
        </div>
      </header>

      <section className="employee-summary-grid">
        <article><span className="summary-icon teal"><Building2 size={21} /></span><div><small>Total branches</small><strong>{branches.length}</strong></div></article>
        <article><span className="summary-icon blue"><MapPin size={21} /></span><div><small>Cities</small><strong>{unique('city').length}</strong></div></article>
        <article><span className="summary-icon purple"><ShieldCheck size={21} /></span><div><small>Active branches</small><strong>{branches.filter((item) => item.status === 'Active').length}</strong></div></article>
        <article><span className="summary-icon orange"><Users size={21} /></span><div><small>Employees mapped</small><strong>{totalEmployees}</strong></div></article>
      </section>

      <section className="employee-filter-panel">
        <div className="filter-panel-head">
          <div>
            <h2>Branch directory</h2>
            <p>Gunakan pencarian dan filter untuk menemukan branch yang sesuai.</p>
          </div>
          <button className="filter-toggle" onClick={() => setShowFilters((value) => !value)}>
            <Filter size={16} /> Filters {activeFilters > 0 && <b>{activeFilters}</b>}
          </button>
        </div>
        <div className="search-row">
          <label className="employee-search">
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPage(1); }}
              placeholder="Search branch, code, city, manager..."
            />
          </label>
          <button className="clear-filter" onClick={reset}>Clear</button>
          <button className="refresh-button" onClick={load} title="Refresh from server"><RefreshCw size={17} /> Refresh</button>
        </div>
        {showFilters && (
          <div className="filter-row department-filter-row">
            <label>City<select value={city} onChange={(event) => select(event, setCity)}><option>All cities</option>{unique('city').map((value) => <option key={value}>{value}</option>)}</select></label>
            <label>Status<select value={status} onChange={(event) => select(event, setStatus)}><option>All statuses</option>{unique('status').map((value) => <option key={value}>{value}</option>)}</select></label>
          </div>
        )}
      </section>

      <section className="employee-table-panel">
        <div className="directory-heading">
          <div>
            <h2>Branches</h2>
            <p>{filtered.length} branch{filtered.length === 1 ? '' : 'es'} shown</p>
          </div>
          <span className="sync-note"><Clock3 size={15} /> Synced from HRIS backend</span>
        </div>
        <div className="table-scroll">
          <table className="employee-table hris-master-table">
            <thead>
              <tr>
                <th>Branch</th>
                <th>City</th>
                <th>Manager</th>
                <th>Employees</th>
                <th>Status</th>
                <th>Address</th>
              </tr>
            </thead>
            <tbody>
              {visible.length ? visible.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="name-cell">
                      <span className="department-avatar"><Building2 size={17} /></span>
                      <span><strong>{item.name}</strong><small>{item.code}</small></span>
                    </div>
                  </td>
                  <td><span className="department-pill">{item.city}</span></td>
                  <td>{item.manager}</td>
                  <td><span className="assignment-count"><Users size={15} /> {item.employeeCount}</span></td>
                  <td><Status value={item.status} /></td>
                  <td>{item.address}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="empty-state">{loading ? 'Loading branches…' : 'Tidak ada branch yang sesuai dengan filter.'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <footer className="table-footer">
          <span>Showing {filtered.length ? ((page - 1) * rowsPerPage) + 1 : 0}-{Math.min(page * rowsPerPage, filtered.length)} of {filtered.length} branches</span>
          <div className="pagination-controls">
            <label>Rows per page<select value={rowsPerPage} onChange={changeRowsPerPage}><option value={5}>5</option><option value={7}>7</option><option value={10}>10</option><option value={25}>25</option></select></label>
            <div className="pager">
              <button disabled={page === 1} onClick={() => setPage((value) => value - 1)} aria-label="Previous page"><ChevronLeft size={18} /></button>
              <b>{page}</b>
              <button disabled={page === totalPages} onClick={() => setPage((value) => value + 1)} aria-label="Next page"><ChevronRight size={18} /></button>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default Branch;
